import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  Avatar,
  Stack,
  SxProps,
  Theme,
  Typography,
  TypographyVariant,
  alpha,
  useTheme,
} from '@mui/material';
import { ReactElement } from 'react';

interface NameObject {
  firstName?: string | null;
  lastName?: string | null;
}

type ProvidedName = string | NameObject | null | undefined;

/**
 * Retrieves the name of a person based on the provided input.
 *
 * If the input is a string, it is returned as-is.
 * If the input is an object with firstName and lastName properties,
 * it returns the concatenated full name.
 * If the input is null, undefined, or falsy, it returns null.
 *
 * @param {string | { firstName: string, lastName: string }} name - The input representing the person's name. It can be either a string or an object containing firstName and lastName properties.
 * @returns {string | null} The person's name as a concatenated string, the string itself if already provided, or null if no valid input is given.
 */
const getPersonName = (name: ProvidedName): string | null =>
  name
    ? typeof name === 'string'
      ? name
      : (name.firstName || '') + ' ' + (name.lastName || '')
    : null;

/**
 * Extracts the initials from a provided name.
 *
 * This function takes a name, processes it to extract individual parts,
 * and returns the initials in uppercase format. If the name is not
 * provided or cannot be processed, it returns null.
 *
 * @param {ProvidedName} name - The input name from which initials are extracted.
 * @returns {string | null} The uppercase initials of the provided name, or null if the name is invalid.
 */
const getInitials = (name: ProvidedName): string | null => {
  const personName = getPersonName(name);
  if (!personName) {
    return null;
  }
  return personName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

interface Props {
  hideAvatar?: boolean;
  avatarColor?: string;
  withTooltip?: boolean;
  name: ProvidedName;
  stackSX?: SxProps<Theme>;
  labelStyle?: TypographyVariant;
  notSetLabel?: string;
}

/**
 * Renders a label component for a person, including an optional avatar and name.
 *
 * @param {Object} props - The properties to configure the PersonLabel component.
 * @param {string} props.name - The full name of the person to display. If a name is not provided, the label displays "Not set".
 * @param {string} [props.avatarColor] - The background color of the avatar. Defaults to the theme's primary color if not specified.
 * @param {boolean} [props.hideAvatar=false] - Whether to hide the avatar. If true, the avatar is not rendered.
 * @param {boolean} [props.withTooltip=false] - Whether to show a tooltip containing the person's full name when hovering over the avatar.
 * @param {string} [props.labelStyle='body1'] - The typography variant for the person's name label.
 * @param {string} [props.notSetLabel='Not set'] - The text to display when the person's name is not provided.
 * @param {Object} [props.stackSX] - Additional style props to pass to the outer Stack component.
 *
 * @return {ReactElement} The rendered PersonLabel component.
 */
export function PersonLabel(props: Props): ReactElement {
  const theme = useTheme();
  const initials = getInitials(props.name);
  const personName = getPersonName(props.name);
  // Ensure a name is set, otherwise the avatar needs to be transparent.
  const avatarColor = props.name
    ? (props.avatarColor ?? theme.palette.primary.main)
    : undefined;
  const typographyVariant = props.labelStyle ?? 'body1';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={props.stackSX}>
      {!props.hideAvatar && (
        <ConditionalTooltip
          tooltipText={personName && props.withTooltip ? personName : undefined}
          arrow
          placement="top"
        >
          <Avatar
            sx={{
              width: `calc(${theme.typography[typographyVariant].fontSize} * ${theme.typography[typographyVariant].lineHeight})`,
              height: `calc(${theme.typography[typographyVariant].fontSize} * ${theme.typography[typographyVariant].lineHeight})`,
              bgcolor: avatarColor
                ? alpha(avatarColor, 0.1)
                : 'rgba(0, 0, 0, 0)',
              color: avatarColor ? avatarColor : 'rgba(0, 0, 0, 0)',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {initials}
          </Avatar>
        </ConditionalTooltip>
      )}
      <Typography
        variant={typographyVariant}
        color={personName ? 'inherit' : 'text.secondary'}
      >
        {personName ?? props.notSetLabel ?? 'Not set'}
      </Typography>
    </Stack>
  );
}
